import { httpRequest } from "@services/http"
import type {
  BudgetTreePaceSegment,
  BudgetTreeAdGroup,
  BudgetTreeLaunch,
  BudgetTreeNetwork,
  EngagementBudgetTree,
} from "@models/engagementBudgetTree.model"

const num = (v: unknown): number => (typeof v === "number" ? v : parseFloat(String(v ?? 0)) || 0)
const intNum = (v: unknown): number => (typeof v === "number" ? v : parseInt(String(v ?? 0), 10) || 0)
const decOrNull = (v: unknown): number | null => (v == null ? null : num(v))

const adGroup = (a: any): BudgetTreeAdGroup => ({
  ad_group_id: intNum(a.ad_group_id),
  ad_group_name: String(a.ad_group_name ?? ""),
  audience_id: intNum(a.audience_id),
  audience_name: String(a.audience_name ?? ""),
  percent: num(a.percent),
  margin: num(a.margin),
  has_explicit_policy: Boolean(a.has_explicit_policy),
  resolved_amount: num(a.resolved_amount),
  active_days: intNum(a.active_days),
  daily_spend_cap: decOrNull(a.daily_spend_cap),
  bid: decOrNull(a.bid),
})

/* Serving ranges, defensively. A response that predates MPN-1802 sends nothing, and the
   engine treats an empty list as "no schedule known" and falls back to calendar days. */
const ranges = (raw: any): [string, string][] =>
  Array.isArray(raw)
    ? raw
        .filter((r) => Array.isArray(r) && r.length === 2)
        .map((r) => [String(r[0]), String(r[1])] as [string, string])
    : []

const network = (n: any): BudgetTreeNetwork => ({
  network: String(n.network ?? ""),
  is_paused: Boolean(n.is_paused),
  percent: num(n.percent),
  margin: num(n.margin),
  has_explicit_policy: Boolean(n.has_explicit_policy),
  resolved_amount: num(n.resolved_amount),
  active_days: intNum(n.active_days),
  // One row is one segment. `policy_source === "pace"` is the server saying this window
  // carries its own percent rather than inheriting the campaign's, which is exactly what
  // a pin is. The flattening below sums these across a launch's segments.
  segment_count: 1,
  pinned_segments: n.policy_source === "pace" ? 1 : 0,
  scheduled_ranges: ranges(n.scheduled_ranges),
  daily_spend_cap: decOrNull(n.daily_spend_cap),
  bid: decOrNull(n.bid),
  bid_strategy: n.bid_strategy != null ? String(n.bid_strategy) : null,
  // Defaults to true, so a response from before MPN-1584 shows the Bid field rather than
  // hiding a working control across every network at once.
  accepts_bid: n.accepts_bid !== false,
  // Same defaulting for MPN-1624: absent means show. This mapper builds the object field
  // by field, so a field missing here is silently undefined on every row downstream -- the
  // first version of MPN-1624 filtered on a flag that never made it past this line.
  is_configured: n.is_configured !== false,
  ad_groups: Array.isArray(n.ad_groups) ? n.ad_groups.map(adGroup) : [],
})

const pct = (amount: number, parent: number): number => (parent > 0 ? (amount / parent) * 100 : 0)

const mergeAdGroups = (lists: BudgetTreeAdGroup[][], parentResolved: number): BudgetTreeAdGroup[] => {
  const byId = new Map<number, BudgetTreeAdGroup>()
  for (const list of lists) {
    for (const a of list) {
      const prev = byId.get(a.ad_group_id)
      if (prev) {
        prev.resolved_amount += a.resolved_amount
        prev.has_explicit_policy ||= a.has_explicit_policy
      } else {
        byId.set(a.ad_group_id, { ...a })
      }
    }
  }
  return [...byId.values()].map((a) => ({ ...a, percent: pct(a.resolved_amount, parentResolved) }))
}

// The API now nests networks under a `pace_segments` layer (launch → pace_segments[] → networks[]),
// and a network's split can differ per segment. We don't surface a pace UI yet, so collapse the
// segments into one network list per launch by AGGREGATING each network across its segments (summing
// amounts, recomputing percent from the launch total). Concatenating would produce duplicate rows and
// percents > 100 once per-pace overrides exist. A launch with no paces yields a single null segment,
// so this is a 1:1 passthrough for it.
const launchNetworks = (l: any, launchResolved: number): BudgetTreeNetwork[] => {
  const segments: any[] = Array.isArray(l.pace_segments) ? l.pace_segments : [{ networks: l.networks }]
  const byNet = new Map<
    string,
    { base: BudgetTreeNetwork; serverPercent: number; adGroupLists: BudgetTreeAdGroup[][] }
  >()
  for (const seg of segments) {
    for (const raw of Array.isArray(seg?.networks) ? seg.networks : []) {
      const n = network(raw)
      const entry = byNet.get(n.network)
      if (entry) {
        entry.base.resolved_amount += n.resolved_amount
        entry.serverPercent += n.percent
        entry.base.has_explicit_policy ||= n.has_explicit_policy
        entry.base.segment_count += n.segment_count
        entry.base.pinned_segments += n.pinned_segments
        // Ranges are the network's own schedule, identical in every segment -- keep the
        // first rather than concatenating duplicates.
        entry.adGroupLists.push(n.ad_groups)
      } else {
        byNet.set(n.network, {
          base: { ...n, ad_groups: [] },
          serverPercent: n.percent,
          adGroupLists: [n.ad_groups],
        })
      }
    }
  }
  return [...byNet.values()].map(({ base, serverPercent, adGroupLists }) => ({
    ...base,
    // A paused network holds no money, so deriving its percent from the amount would show
    // 0% and lose the share it gets back when it is switched on (MPN-1486). Keep what the
    // server sent for those; everything else is still derived from the amount, which is
    // what makes per-pace splits add up.
    percent: base.is_paused ? serverPercent : pct(base.resolved_amount, launchResolved),
    ad_groups: mergeAdGroups(adGroupLists, base.resolved_amount),
  }))
}

// The segments themselves, kept alongside the flattened view above. The flat list is what
// the legacy editor reads; the segmented screen needs the windows and their own per-segment
// network splits, which the aggregation deliberately destroys.
const paceSegment = (s: any): BudgetTreePaceSegment => ({
  segment_id: intNum(s.segment_id),
  segment_name: String(s.segment_name ?? ""),
  percent: num(s.percent),
  resolved_amount: num(s.resolved_amount),
  start_time: s.start_time != null ? String(s.start_time) : null,
  end_time: s.end_time != null ? String(s.end_time) : null,
  networks: Array.isArray(s.networks) ? s.networks.map(network) : [],
})

const launch = (l: any): BudgetTreeLaunch => {
  const resolved = num(l.resolved_amount)
  return {
    launch_id: intNum(l.launch_id),
    launch_name: String(l.launch_name ?? ""),
    percent: num(l.percent),
    margin: num(l.margin),
    allocation: num(l.allocation),
    scheduled_ranges: ranges(l.scheduled_ranges),
    target_amount: num(l.target_amount),
    // A response predating MPN-1802 sends nothing, and a campaign with no pattern is by
    // definition one that has never been customised.
    spend_pattern: String(l.spend_pattern ?? "LEGACY"),
    go_live_date: l.go_live_date != null ? String(l.go_live_date) : null,
    campaign_start: l.campaign_start != null ? String(l.campaign_start) : null,
    campaign_end: l.campaign_end != null ? String(l.campaign_end) : null,
    resolved_amount: resolved,
    networks: launchNetworks(l, resolved),
    pace_segments: Array.isArray(l.pace_segments) ? l.pace_segments.map(paceSegment) : [],
  }
}

export class CommandGetEngagementBudgetTree {
  async run(engagementId: number): Promise<EngagementBudgetTree> {
    const url = `${import.meta.env.VITE_API_PLATFORM_SERVICE_URL}api/v1/engagements/${engagementId}/budget-tree/`
    const r = await httpRequest<any>(url, { method: "GET" })
    return {
      engagement_id: intNum(r.engagement_id),
      balance: num(r.balance),
      // Falls back to the balance for a server that predates MPN-1789, so the split
      // still adds up to something rather than to zero.
      remaining_flight_funding: r.remaining_flight_funding != null ? num(r.remaining_flight_funding) : num(r.balance),
      is_projected: Boolean(r.is_projected),
      launches: Array.isArray(r.launches) ? r.launches.map(launch) : [],
    }
  }
}
